import React, { useState, useEffect } from 'react';
import { savingsProductsApi, api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { PiggyBank, Plus, Edit, FileText, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const SavingsProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', code: '', description: '', account_type: 'savings',
    min_opening_balance: '', interest_rate: '',
    interest_calculation_method: 'daily_balance', compounding_period: 'monthly',
    posting_period: 'monthly', min_balance_for_interest: 0,
    lock_in_period_days: 0, withdrawal_fee_amount: 0, monthly_fee: 0
  });
  const [editProduct, setEditProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [contractFile, setContractFile] = useState(null);
  const contractInputRef = React.useRef(null);
  const { user } = useAuth();

  useEffect(() => { load(); loadPending(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await savingsProductsApi.list(); setProducts(res.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/pending-product-changes');
      setPendingChanges(res.data.filter(c => c.product_type === 'savings_product'));
    } catch (e) { /* ignore */ }
  };

  const handleSubmit = async () => {
    try {
      await savingsProductsApi.create({
        ...form,
        min_opening_balance: parseFloat(form.min_opening_balance) || 0,
        interest_rate: parseFloat(form.interest_rate) || 0,
        min_balance_for_interest: parseFloat(form.min_balance_for_interest) || 0,
        lock_in_period_days: parseInt(form.lock_in_period_days) || 0,
        withdrawal_fee_amount: parseFloat(form.withdrawal_fee_amount) || 0,
        monthly_fee: parseFloat(form.monthly_fee) || 0,
      });
      toast.success('Savings product created');
      setShowModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const openEditModal = (p) => {
    setEditProduct(p);
    setEditForm({
      name: p.name || '', description: p.description || '', account_type: p.account_type || 'savings',
      min_opening_balance: p.min_opening_balance || '', interest_rate: p.interest_rate || '',
      compounding_period: p.compounding_period || 'monthly', posting_period: p.posting_period || 'monthly',
      lock_in_period_days: p.lock_in_period_days || 0, monthly_fee: p.monthly_fee || 0,
      effective_date: new Date().toISOString().slice(0, 10),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const payload = {
        ...editForm,
        min_opening_balance: parseFloat(editForm.min_opening_balance) || undefined,
        interest_rate: parseFloat(editForm.interest_rate) || undefined,
        lock_in_period_days: parseInt(editForm.lock_in_period_days) || 0,
        monthly_fee: parseFloat(editForm.monthly_fee) || 0,
      };
      const res = await api.put(`/savings-products/${editProduct.id}`, payload);
      if (res.data.status === 'pending') {
        toast.success('Product change submitted for checker approval');
      } else {
        toast.success('Product updated');
      }
      setShowEditModal(false);
      load();
      loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleApprove = async (changeId) => {
    try {
      await api.post(`/pending-product-changes/${changeId}/approve`, { comments: '' });
      toast.success('Product change approved');
      loadPending(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReject = async (changeId) => {
    try {
      await api.post(`/pending-product-changes/${changeId}/reject`, { comments: '' });
      toast.success('Product change rejected');
      loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
  };

  const handleContractUpload = async (productId) => {
    if (!contractFile) return;
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const b64 = e.target.result.split(',')[1];
        await api.post(`/savings-products/${productId}/contract/upload`, {
          file_data: b64, file_name: contractFile.name, file_type: contractFile.type,
        });
        toast.success('Contract uploaded');
        setContractFile(null);
        load();
      };
      reader.readAsDataURL(contractFile);
    } catch (e) { toast.error('Failed to upload contract'); }
  };

  const typeLabels = { savings: 'Savings', current: 'Current', fixed_deposit: 'Fixed Deposit', recurring_deposit: 'Recurring Deposit' };

  return (
    <div className="space-y-6 fade-in" data-testid="savings-products-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>Products</span><span>/</span><span className="text-[#1f1f1f] font-medium">Savings Products</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">PRODUCT CONFIGURATION</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Savings Products</h1>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-savings-product-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Savings Product
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Code</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Min Opening</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Interest Rate</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Lock-in</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            products.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">No savings products.</td></tr> :
            products.map(p => (
              <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`sp-row-${p.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{p.name}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{p.code}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">{typeLabels[p.account_type] || p.account_type}</span></td>
                <td className="px-4 py-3 text-sm text-gray-600">R {(p.min_opening_balance || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-sm font-medium">{p.interest_rate || 0}%</td>
                <td className="px-4 py-3 text-sm text-gray-600">{p.lock_in_period_days || 0} days</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${p.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{p.is_active !== false ? 'Active' : 'Inactive'}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-gray-500 hover:text-[#1f1f1f]" onClick={() => openEditModal(p)} data-testid={`sp-edit-${p.id}`}>Edit</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="sp-form-desc">
          <DialogHeader><DialogTitle>Create Savings Product</DialogTitle></DialogHeader>
          <p id="sp-form-desc" className="sr-only">Form to create savings product</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Product Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="sp-name" /></div>
              <div><Label>Short Code *</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} data-testid="sp-code" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="sp-desc" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Account Type *</Label>
                <Select value={form.account_type} onValueChange={v => setForm({...form, account_type: v})}>
                  <SelectTrigger data-testid="sp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="current">Current Account</SelectItem>
                    <SelectItem value="fixed_deposit">Fixed Deposit</SelectItem>
                    <SelectItem value="recurring_deposit">Recurring Deposit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Min Opening Balance (ZAR)</Label><Input type="number" value={form.min_opening_balance} onChange={e => setForm({...form, min_opening_balance: e.target.value})} data-testid="sp-min-bal" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm({...form, interest_rate: e.target.value})} data-testid="sp-rate" /></div>
              <div><Label>Compounding</Label>
                <Select value={form.compounding_period} onValueChange={v => setForm({...form, compounding_period: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Posting</Label>
                <Select value={form.posting_period} onValueChange={v => setForm({...form, posting_period: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="annually">Annually</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Lock-in Period (days)</Label><Input type="number" value={form.lock_in_period_days} onChange={e => setForm({...form, lock_in_period_days: e.target.value})} data-testid="sp-lockin" /></div>
              <div><Label>Monthly Fee (ZAR)</Label><Input type="number" value={form.monthly_fee} onChange={e => setForm({...form, monthly_fee: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="sp-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="sp-edit-desc">
          <DialogHeader><DialogTitle>Edit: {editProduct?.name}</DialogTitle></DialogHeader>
          <p id="sp-edit-desc" className="sr-only">Form to edit a savings product</p>
          {editProduct && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                {user?.role === 'admin' ? 'Admin: changes apply immediately.' : 'Changes will be submitted for checker approval.'}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Product Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                <div><Label>Account Type</Label>
                  <Select value={editForm.account_type} onValueChange={v => setEditForm({...editForm, account_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="savings">Savings</SelectItem><SelectItem value="current">Current</SelectItem>
                      <SelectItem value="fixed_deposit">Fixed Deposit</SelectItem><SelectItem value="recurring_deposit">Recurring Deposit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Input value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={editForm.interest_rate} onChange={e => setEditForm({...editForm, interest_rate: e.target.value})} /></div>
                <div><Label>Min Opening Balance</Label><Input type="number" value={editForm.min_opening_balance} onChange={e => setEditForm({...editForm, min_opening_balance: e.target.value})} /></div>
                <div><Label>Effective Date</Label><Input type="date" value={editForm.effective_date} onChange={e => setEditForm({...editForm, effective_date: e.target.value})} data-testid="sp-edit-effective-date" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Lock-in (days)</Label><Input type="number" value={editForm.lock_in_period_days} onChange={e => setEditForm({...editForm, lock_in_period_days: e.target.value})} /></div>
                <div><Label>Monthly Fee</Label><Input type="number" value={editForm.monthly_fee} onChange={e => setEditForm({...editForm, monthly_fee: e.target.value})} /></div>
              </div>
              {/* Contract Upload */}
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <div className="p-3 border border-gray-200 bg-gray-50 space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">T&C / Contract Document</Label>
                  {editProduct.contract_file_name && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <FileText className="w-3.5 h-3.5" />
                      <span>Current: {editProduct.contract_file_name} (v{editProduct.contract_version})</span>
                    </div>
                  )}
                  <div className="flex gap-2 items-center">
                    <Input type="file" accept=".pdf,.doc,.docx" ref={contractInputRef} onChange={e => setContractFile(e.target.files?.[0])} className="text-xs" />
                    <Button size="sm" disabled={!contractFile} onClick={() => handleContractUpload(editProduct.id)} className="text-xs bg-[#1f1f1f] text-white" data-testid="sp-upload-contract-btn">Upload</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} className="bg-[#1f1f1f] text-white" data-testid="sp-edit-submit">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Changes Queue */}
      {pendingChanges.length > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
        <div className="bg-white border border-amber-200 p-4" data-testid="sp-pending-changes-section">
          <h3 className="text-sm font-bold text-[#1f1f1f] mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" />Pending Savings Product Changes ({pendingChanges.length})
          </h3>
          <div className="space-y-2">
            {pendingChanges.map(pc => (
              <div key={pc.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100" data-testid={`sp-pending-${pc.id}`}>
                <div>
                  <p className="text-sm font-medium">{pc.product_name} ({pc.product_code})</p>
                  <p className="text-xs text-gray-500">By: {pc.maker_name} ({pc.maker_role}) | Effective: {pc.effective_date?.slice(0,10)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Changes: {Object.entries(pc.changes || {}).map(([k,v]) => `${k}: ${v}`).join(', ')}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApprove(pc.id)} className="bg-green-600 text-white text-xs" data-testid={`sp-approve-${pc.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => handleReject(pc.id)} className="text-xs text-red-600 border-red-200" data-testid={`sp-reject-${pc.id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SavingsProductsPage;
